// Perishable or pantry? Decides which carts an item can go to (regular Amazon only takes
// pantry and household items). A staple's own setting wins; otherwise we guess from the name,
// and the guess is shown so it can be corrected once.

import type { Staple, StoreCarries, StoreSettings } from './types.ts';
import { normalizeName } from './text.ts';

// Words are matched at word starts in the singular, lowercased name (see normalizeName).
// Checked first: these win over pantry words ("oat milk", "frozen berries", "bell pepper").
const ALWAYS_PERISHABLE = ['milk', 'yogurt', 'cheese', 'cream', 'egg', 'frozen', 'ice cream', 'bell pepper', 'watermelon'];

const PANTRY = [
  'dried', 'canned', 'powder', 'granola', 'cereal', 'cracker', 'chip', 'pasta', 'rice', 'flour', 'sugar',
  'oil', 'vinegar', 'sauce', 'spice', 'salt', 'pepper', 'coffee', 'tea', 'oat', 'nut', 'almond', 'peanut butter',
  'honey', 'jam', 'syrup', 'bean', 'lentil', 'protein bar', 'snack', 'cookie', 'chocolate', 'water', 'sparkling',
  'paper', 'towel', 'tissue', 'toilet', 'soap', 'detergent', 'dish', 'sponge', 'trash bag', 'foil', 'wrap', 'shampoo',
  'toothpaste', 'battery', 'vitamin', 'supplement', 'wipe', 'diaper', 'cleaner',
];

function has(name: string, words: string[]): boolean {
  const n = ` ${normalizeName(name)}`;
  return words.some((w) => n.includes(` ${normalizeName(w)}`));
}

/**
 * Best guess from the name alone. Unknown names count as perishable: a pantry item sent to
 * Fresh costs a little more, but food sent to the slow cart could spoil.
 */
export function guessPerishable(name: string): boolean {
  if (has(name, ALWAYS_PERISHABLE)) return true;
  if (has(name, PANTRY)) return false;
  return true;
}

export function isPerishable(staple: Pick<Staple, 'name' | 'perishable'>): boolean {
  return staple.perishable ?? guessPerishable(staple.name);
}

export function storeTakes(carries: StoreCarries | undefined, perishable: boolean): boolean {
  if (!carries || carries === 'any') return true;
  return carries === (perishable ? 'perishable' : 'pantry');
}

/** Stores an item of this kind can be ordered from. */
export function storesFor(stores: StoreSettings[], perishable: boolean): StoreSettings[] {
  return stores.filter((s) => storeTakes(s.carries, perishable));
}

export function kindLabel(perishable: boolean): string {
  return perishable ? 'Perishable' : 'Pantry or household';
}
